// MMO 공유 필드 프로토콜(REBIRTH-MODES-SPEC 4장, 레인 M). 서버(src/server/field.ts FieldShard)와 클라(src/game/fieldNet.ts)가 같이 쓴다.
// - 이진 메시지: [버전 u8][종류 u8][본문…], 리틀 엔디언. 좌표는 1/16m 정수(i16, ±2047m), 방향은 u8(2π/256).
// - 인코더는 미리 잡아 둔 버퍼 하나(FieldWriter)에 쓰고 길이만 돌려준다(메시지마다 버퍼를 새로 만들지 않는다).
// - 관심 영역: 32m 청크 3×3(자기 청크 ±1). 채널 규칙: 채널당 최대 40명, 넘치면 다음 채널.
// 순수 데이터·함수만 둔다(DOM·Workers API 없음).
import { DAMAGE_CAP } from './config';

/** 프로토콜 버전. 메시지 모양이 바뀌면 올린다(서버는 다른 버전의 클라를 Error(Version)로 돌려보낸다) */
export const FIELD_PROTO_VERSION = 1;

/** 메시지 종류(C = 클라 → 서버, S = 서버 → 클라) */
export const FieldMsg = {
  // C → S
  Join: 1, Pos: 2, Hit: 3,
  // S → C
  Welcome: 10, PeerAdd: 11, PeerLeave: 12, Snap: 13, Boss: 14, BossResult: 15, Correct: 16, Error: 17,
} as const;
export type FieldMsgId = (typeof FieldMsg)[keyof typeof FieldMsg];

/** Error 메시지 코드 */
export const FieldErr = { Version: 1, BadJoin: 2, Full: 3, Kicked: 4, Server: 5, Idle: 6 } as const;

/** 필드 구역(1차 범위: 지역1 이끼 유적 필드 한 구역). 레인 F 구역이 늘면 여기에 붙인다 */
export const FIELD_ZONES: readonly string[] = ['r1'];
/** 채널당 최대 인원(스펙 4.1) */
export const FIELD_CHANNEL_CAPACITY = 40;
/** 구역당 채널 수 상한(40 × 10 = 400명). 다 차면 입장 거부(field-full) */
export const FIELD_MAX_CHANNELS = 10;
/** 무입력(유효한 바이너리 메시지 없음) 이 시간(ms)이 지나면 Idle로 내보낸다 — 유휴·게스트 소켓이 정원을 점유하지 않게(PR #237 P2) */
export const FIELD_IDLE_MS = 300_000;
/** 채널(DO) 하나에 같은 IP 해시로 동시에 둘 수 있는 연결 수(통신사 공유 IP·PC방을 생각해 넉넉히) */
export const FIELD_IP_MAX = 8;
/** Worker가 한 번의 입장에서 깨우는 DO 수 상한(가득 찬 채널은 짧게 캐시해 건너뛴다) */
export const FIELD_CHANNEL_TRIES = 3;
/** 가득 찬 채널 표시를 Worker isolate가 기억하는 시간(ms) */
export const FIELD_FULL_CACHE_MS = 5_000;
/** 서버가 정한 구역 입구(스폰) = 필드 구역1 지도 스폰(shared/field/layout fieldZone1().spawn, 시험이 같은지 확인). 클라가 보낸 join 좌표는 이 반지름(m) 안일 때만 받는다(보스 중심에서 range 밖) */
export const FIELD_SPAWN = { x: 0.5, z: 71, radius: 8 } as const;
/** 같은 계정이 이 시간(ms) 안에 다시 들어오면 마지막 위치에서 이어 시작한다(그보다 오래면 입구) */
export const FIELD_RESUME_MS = 10 * 60_000;
/** 관심 영역 청크 한 변(m). 레인 F 필드 청크(32m)와 같다 */
export const FIELD_CHUNK_M = 32;
/** 좌표 양자화 배율(1/16m)과 허용 범위(m) */
export const FIELD_Q = 16;
export const FIELD_COORD_LIMIT = 2000;
/** 위치 송신·스냅 주기(ms) = 10Hz. 클라는 바뀐 것이 있을 때만 보낸다 */
export const FIELD_TICK_MS = 100;
/**
 * 서 있을 때의 생존 확인 주기(ms). 텍스트 'ping'은 런타임 자동 응답('pong')이 받아 DO를 깨우지 않는다(비용).
 * 서 있는 사람은 위치를 다시 보내지 않는다.
 */
export const FIELD_KEEPALIVE_MS = 25_000;
/** 서 있어도 이 간격마다 유효한 pos 한 건을 보낸다(서버 Idle은 텍스트 ping을 입력으로 세지 않는다 — 서 있는 사람이 FIELD_IDLE_MS에 쫓겨나지 않게). FIELD_IDLE_MS보다 충분히 짧아야 한다 */
export const FIELD_POS_HEARTBEAT_MS = 60_000;
/** 필드 쪽 WebSocket 경로(같은 출처). 구역·채널은 쿼리 zone·ch */
export const FIELD_WS_PATH = '/api/field/ws';
/** 이름 최대 글자 수(UTF-8 36바이트 안) */
export const FIELD_NAME_MAX = 12;

/**
 * 속도 상한(순간이동 거부). 이동 거리를 '거리 토큰 통'으로 센다: 초당 maxSpeed(m)씩 차고 burst(m)까지 쌓인다.
 * 달리기 7m/s(+광란 10%), 회피 4m/0.35s, 돌진·도약 스킬의 짧은 몰아치기를 burst가 받아 준다. 통보다 먼 이동은 거부한다.
 */
export const FIELD_MOVE = { maxSpeed: 12, burst: 10, epsilon: 0.25 } as const;
/** 클라가 보내는 pos 최소 간격(ms) — 이보다 잦은 것은 버린다(10Hz 클라 + 지터 여유) */
export const FIELD_POS_MIN_GAP_MS = 40;

/** 월드 보스 공통 규칙(가안 — 시뮬로 확정). 등장: 매시 정각·30분, 10분 안에 못 잡으면 떠난다 */
export const FIELD_BOSS = {
  periodMs: 30 * 60_000,
  aliveMs: 10 * 60_000,
  maxHp: 600_000,
  /** hit 한 번 피해 상한(게임 맥뎀과 같다). 넘으면 거부 */
  hitMax: DAMAGE_CAP,
  /** hit 빈도 통: 초당 hitsPerSec개, 최대 hitBurst개 */
  hitsPerSec: 12,
  hitBurst: 24,
  /** 피해 통(접속별, 2026-10-28 서버 리뷰 P1): 초당 dpsCap만큼 차고 최대 dmgBurst(맥뎀 한 방). 넘는 피해는 남은 만큼만 인정 — 한 사람이 맥뎀 연타로 보스를 몇 초에 지우지 못하게(혼자 60만 ≈ 25초+) */
  dpsCap: 20_000,
  dmgBurst: DAMAGE_CAP,
  /** 보스 중심에서 이 거리(m) 안에서만 피해를 받는다(원거리 스킬 포함) */
  range: 24,
  x: 0,
  z: 0,
  name: '이끼 거신 고르마',
} as const;
export type FieldBossConfig = { -readonly [K in keyof typeof FIELD_BOSS]: (typeof FIELD_BOSS)[K] extends string ? string : number };

/** 월드 보스 상태 */
export const BossPhase = { None: 0, Alive: 1, Dead: 2, Escaped: 3 } as const;

/** 기여도 보상 등급(가안). 1 = S(1위), 2 = A(2~3위), 3 = B(5% 이상), 4 = C(1% 이상), 0 = 없음 */
export const BossTier = { None: 0, S: 1, A: 2, B: 3, C: 4 } as const;
export const BOSS_TIER_LABEL = ['-', 'S', 'A', 'B', 'C'] as const;
/** 등급별 보상 표(가안, 지급 연결은 후속 — 지금은 장부 기록만) */
export const BOSS_REWARDS: readonly { gold: number; chest: string }[] = [
  { gold: 0, chest: '' },
  { gold: 5000, chest: 'boss-gold' },
  { gold: 3000, chest: 'boss-silver' },
  { gold: 1500, chest: 'boss-bronze' },
  { gold: 500, chest: '' },
];

export interface BossContribution { dmg: number }
export interface BossRank { index: number; rank: number; ratio: number; tier: number }

/**
 * 기여도 순위표: 피해 내림차순(같으면 먼저 들어온 순서), 비율 = 피해 / 전체, 등급 규칙 위.
 * 피해 0인 참가자는 등급 없음. 결과는 입력 index를 담는다.
 */
export function bossRewardTable(entries: readonly BossContribution[]): BossRank[] {
  let total = 0;
  for (const e of entries) total += Math.max(0, e.dmg);
  const order = entries.map((e, index) => ({ e, index })).sort((a, b) => b.e.dmg - a.e.dmg || a.index - b.index);
  return order.map(({ e, index }, i) => {
    const ratio = total > 0 ? Math.max(0, e.dmg) / total : 0;
    const rank = i + 1;
    const tier = e.dmg <= 0 ? BossTier.None : rank === 1 ? BossTier.S : rank <= 3 ? BossTier.A : ratio >= 0.05 ? BossTier.B : ratio >= 0.01 ? BossTier.C : BossTier.None;
    return { index, rank, ratio, tier };
  });
}

/** 정해진 등장 시각: now가 속한 칸의 시작(ms) */
export function bossSlotOf(now: number, periodMs: number = FIELD_BOSS.periodMs): number {
  return Math.floor(now / periodMs) * periodMs;
}
/** 다음 등장 시각(ms). 지금 칸이 아직 열려 있어도 다음 칸 시작을 돌려준다 */
export function nextBossAt(now: number, periodMs: number = FIELD_BOSS.periodMs): number {
  return bossSlotOf(now, periodMs) + periodMs;
}

// ── 관심 영역 ──────────────────────────────────────────────

export function chunkOf(v: number): number { return Math.floor(v / FIELD_CHUNK_M); }
/** 두 위치가 서로의 3×3 청크 안인가 */
export function inInterest(ax: number, az: number, bx: number, bz: number): boolean {
  return Math.abs(chunkOf(ax) - chunkOf(bx)) <= 1 && Math.abs(chunkOf(az) - chunkOf(bz)) <= 1;
}

// ── 채널 ──────────────────────────────────────────────────

export function validZone(zone: unknown): zone is string { return typeof zone === 'string' && FIELD_ZONES.includes(zone); }
/** DO 이름: field:<구역>:<채널> */
export function channelName(zone: string, channel: number): string { return 'field:' + zone + ':' + channel; }
/** 1부터. 범위 밖이면 1 */
export function parseChannel(v: unknown): number {
  const n = typeof v === 'string' && /^\d{1,2}$/.test(v) ? Number(v) : typeof v === 'number' ? v : 1;
  return Number.isInteger(n) && n >= 1 && n <= FIELD_MAX_CHANNELS ? n : 1;
}
/** 넘침 안내 순서: 요청한 채널부터 끝까지, 다시 1부터(i번째 시도의 채널) */
export function channelAttempt(start: number, i: number): number { return ((start - 1 + i) % FIELD_MAX_CHANNELS) + 1; }

// ── 속도 상한 ─────────────────────────────────────────────

export interface MoveGuard { budget: number; at: number }
/**
 * 이동 검사: 지난 검사 뒤 흐른 시간만큼 통을 채우고 거리를 뺀다. 통보다 멀면 거부(통은 그대로 — 다음 정상 이동은 받는다).
 * 시간은 서버 시계(ms). 클라 시각은 믿지 않는다.
 */
export function checkMove(g: MoveGuard, now: number, dist: number, rule: { maxSpeed: number; burst: number; epsilon: number } = FIELD_MOVE): boolean {
  const dt = Math.max(0, now - g.at) / 1000;
  g.budget = Math.min(rule.burst, g.budget + dt * rule.maxSpeed);
  g.at = now;
  if (!(dist >= 0) || dist > g.budget + rule.epsilon) return false;
  g.budget = Math.max(0, g.budget - dist);
  return true;
}

/** 빈도 통(hit). 성공하면 1 소비 */
export interface RateBucket { tokens: number; at: number }
export function takeToken(b: RateBucket, now: number, perSec: number, burst: number): boolean {
  b.tokens = Math.min(burst, b.tokens + Math.max(0, now - b.at) / 1000 * perSec);
  b.at = now;
  if (b.tokens < 1) return false;
  b.tokens -= 1;
  return true;
}

// ── 양자화 ────────────────────────────────────────────────

const TAU = Math.PI * 2;
export function qCoord(v: number): number {
  const q = Math.round(v * FIELD_Q);
  return q > 32767 ? 32767 : q < -32768 ? -32768 : q;
}
export function dqCoord(q: number): number { return q / FIELD_Q; }
export function qFacing(f: number): number {
  if (!Number.isFinite(f)) return 0;
  return Math.round((((f % TAU) + TAU) % TAU) / TAU * 256) & 255;
}
export function dqFacing(q: number): number { return (q & 255) / 256 * TAU; }
export function validCoord(v: number): boolean { return Number.isFinite(v) && Math.abs(v) <= FIELD_COORD_LIMIT; }

/** 이름 정리: 제어 문자·앞뒤 공백 제거, 글자 수 상한. 비면 기본값 */
export function cleanName(name: unknown, fallback = '모험가'): string {
  if (typeof name !== 'string') return fallback;
  // 제어 문자·서식 문자(방향 바꾸기 등)는 지운다
  const t = name.replace(/[\u0000-\u001f\u007f-\u009f​-‏‪-‮⁦-⁩]/g, '').trim();
  const chars = Array.from(t).slice(0, FIELD_NAME_MAX).join('');
  return chars || fallback;
}

// ── 인코더 ────────────────────────────────────────────────

const UTF8 = new TextEncoder();
const UTF8_DEC = new TextDecoder();

/** 미리 잡아 둔 버퍼 하나에 메시지를 쓴다. 각 write*는 길이를 돌려주고, 보낼 때는 view(len)을 쓴다 */
export class FieldWriter {
  readonly buf: ArrayBuffer;
  readonly u8: Uint8Array;
  readonly dv: DataView;
  private o = 0;
  constructor(size = 2048) {
    this.buf = new ArrayBuffer(size);
    this.u8 = new Uint8Array(this.buf);
    this.dv = new DataView(this.buf);
  }
  private readonly views: Uint8Array[] = [];
  /** 0..len 구간 보기(전송용, 길이별로 한 번만 만든다). 같은 버퍼를 다음 메시지가 덮어쓰므로 send 직후에만 유효하다 */
  view(len: number): Uint8Array { return (this.views[len] ??= this.u8.subarray(0, len)); }
  /** 0..len 구간 복사(테스트·보관용) */
  copy(len: number): Uint8Array { return this.u8.slice(0, len); }

  private head(type: number): void { this.u8[0] = FIELD_PROTO_VERSION; this.u8[1] = type; this.o = 2; }
  private u8w(v: number): void { this.u8[this.o++] = v & 255; }
  private u16(v: number): void { this.dv.setUint16(this.o, v & 0xffff, true); this.o += 2; }
  private i16(v: number): void { this.dv.setInt16(this.o, v, true); this.o += 2; }
  private u32(v: number): void { this.dv.setUint32(this.o, Math.max(0, Math.min(0xffffffff, Math.floor(v))), true); this.o += 4; }
  private f64(v: number): void { this.dv.setFloat64(this.o, v, true); this.o += 8; }
  private name(s: string): void {
    const at = this.o++;
    const r = UTF8.encodeInto(s, this.u8.subarray(this.o, this.o + 36));
    this.u8[at] = r.written;
    this.o += r.written;
  }

  // C → S
  join(hero: number, cycle: number, look: number, facing: number, x: number, z: number, name: string): number {
    this.head(FieldMsg.Join);
    this.u8w(hero); this.u8w(cycle); this.u8w(look); this.u8w(qFacing(facing));
    this.i16(qCoord(x)); this.i16(qCoord(z));
    this.name(name);
    return this.o;
  }
  pos(seq: number, x: number, z: number, facing: number, act: number): number {
    this.head(FieldMsg.Pos);
    this.u16(seq); this.i16(qCoord(x)); this.i16(qCoord(z)); this.u8w(qFacing(facing)); this.u8w(act);
    return this.o;
  }
  hit(bossId: number, dmg: number): number {
    this.head(FieldMsg.Hit);
    this.u16(bossId); this.u32(dmg);
    return this.o;
  }

  // S → C
  welcome(you: number, channel: number, count: number, x: number, z: number, serverNow: number): number {
    this.head(FieldMsg.Welcome);
    this.u16(you); this.u8w(channel); this.u8w(count); this.i16(qCoord(x)); this.i16(qCoord(z)); this.f64(serverNow);
    return this.o;
  }
  peerAdd(id: number, hero: number, cycle: number, look: number, count: number, name: string): number {
    this.head(FieldMsg.PeerAdd);
    this.u16(id); this.u8w(hero); this.u8w(cycle); this.u8w(look); this.u8w(count);
    this.name(name);
    return this.o;
  }
  peerLeave(id: number, count: number): number {
    this.head(FieldMsg.PeerLeave);
    this.u16(id); this.u8w(count);
    return this.o;
  }
  /** 스냅 머리: 이어서 snapEntry를 n번 부른 뒤 snapEnd(n)으로 개수를 채운다 */
  snapBegin(tick: number): void {
    this.head(FieldMsg.Snap);
    this.u16(tick); this.u8w(0);
  }
  snapEntry(id: number, x: number, z: number, facing: number, act: number): void {
    this.u16(id); this.i16(qCoord(x)); this.i16(qCoord(z)); this.u8w(qFacing(facing)); this.u8w(act);
  }
  snapEnd(n: number): number { this.u8[4] = n; return this.o; }
  boss(id: number, phase: number, hp: number, maxHp: number, x: number, z: number, nextAt: number, endsAt: number): number {
    this.head(FieldMsg.Boss);
    this.u16(id); this.u8w(phase); this.u32(hp); this.u32(maxHp); this.i16(qCoord(x)); this.i16(qCoord(z)); this.f64(nextAt); this.f64(endsAt);
    return this.o;
  }
  /** 결과 머리(받는 사람 자기 줄 포함): 이어서 resultEntry n번 → resultEnd(n) */
  resultBegin(bossId: number, total: number, meRank: number, meTier: number, meRatio: number, meDmg: number): void {
    this.head(FieldMsg.BossResult);
    this.u16(bossId); this.u32(total); this.u8w(meRank); this.u8w(meTier); this.u16(Math.round(meRatio * 10000)); this.u32(meDmg); this.u8w(0);
  }
  resultEntry(rank: number, tier: number, ratio: number, dmg: number, name: string): void {
    this.u8w(rank); this.u8w(tier); this.u16(Math.round(ratio * 10000)); this.u32(dmg); this.name(name);
  }
  resultEnd(n: number): number { this.u8[16] = n; return this.o; }
  correct(x: number, z: number): number {
    this.head(FieldMsg.Correct);
    this.i16(qCoord(x)); this.i16(qCoord(z));
    return this.o;
  }
  error(code: number): number {
    this.head(FieldMsg.Error);
    this.u8w(code);
    return this.o;
  }
}

// ── 디코더 ────────────────────────────────────────────────

/** 읽기 커서. reset(data)마다 DataView 하나만 만든다 */
export class FieldReader {
  private dv: DataView = new DataView(new ArrayBuffer(8));
  private u8: Uint8Array = new Uint8Array(8);
  o = 0;
  len = 0;
  /** 머리를 읽는다. 버전이 다르거나 너무 짧으면 type = -1, version은 읽은 값 */
  version = 0;
  type = -1;
  reset(data: ArrayBuffer | ArrayBufferView): this {
    if (data instanceof ArrayBuffer) { this.dv = new DataView(data); this.u8 = new Uint8Array(data); }
    else { this.dv = new DataView(data.buffer, data.byteOffset, data.byteLength); this.u8 = new Uint8Array(data.buffer, data.byteOffset, data.byteLength); }
    this.len = this.dv.byteLength;
    this.o = 2;
    if (this.len < 2) { this.version = 0; this.type = -1; return this; }
    this.version = this.u8[0];
    this.type = this.u8[1];
    return this;
  }
  /** 남은 길이가 n 이상인가 */
  has(n: number): boolean { return this.o + n <= this.len; }
  u8r(): number { return this.u8[this.o++]; }
  u16(): number { const v = this.dv.getUint16(this.o, true); this.o += 2; return v; }
  i16(): number { const v = this.dv.getInt16(this.o, true); this.o += 2; return v; }
  u32(): number { const v = this.dv.getUint32(this.o, true); this.o += 4; return v; }
  f64(): number { const v = this.dv.getFloat64(this.o, true); this.o += 8; return v; }
  coord(): number { return dqCoord(this.i16()); }
  facing(): number { return dqFacing(this.u8r()); }
  /** 이름(길이 u8 + UTF-8). 길이가 넘치면 null */
  name(): string | null {
    if (!this.has(1)) return null;
    const n = this.u8r();
    if (n > 36 || !this.has(n)) return null;
    const s = UTF8_DEC.decode(this.u8.subarray(this.o, this.o + n));
    this.o += n;
    return s;
  }
}

/** 메시지별 최소 길이(머리 2바이트 포함). 디코드 전에 확인한다 */
export const FIELD_MIN_LEN: Readonly<Record<number, number>> = {
  [FieldMsg.Join]: 11, [FieldMsg.Pos]: 10, [FieldMsg.Hit]: 8,
  [FieldMsg.Welcome]: 18, [FieldMsg.PeerAdd]: 9, [FieldMsg.PeerLeave]: 5, [FieldMsg.Snap]: 5,
  [FieldMsg.Boss]: 33, [FieldMsg.BossResult]: 17, [FieldMsg.Correct]: 6, [FieldMsg.Error]: 3,
};
/** 스냅 항목 하나의 바이트 수 */
export const SNAP_ENTRY_BYTES = 8;

/** 받은 Join 본문(서버가 재사용하는 출력 묶음) */
export interface JoinMsg { hero: number; cycle: number; look: number; facing: number; x: number; z: number; name: string }
export function readJoin(r: FieldReader, out: JoinMsg): boolean {
  if (!r.has(9)) return false;
  out.hero = r.u8r(); out.cycle = r.u8r(); out.look = r.u8r(); out.facing = r.facing();
  out.x = r.coord(); out.z = r.coord();
  const name = r.name();
  if (name === null) return false;
  out.name = name;
  return true;
}
export interface PosMsg { seq: number; x: number; z: number; facing: number; act: number }
export function readPos(r: FieldReader, out: PosMsg): boolean {
  if (!r.has(8)) return false;
  out.seq = r.u16(); out.x = r.coord(); out.z = r.coord(); out.facing = r.facing(); out.act = r.u8r();
  return true;
}
export interface HitMsg { bossId: number; dmg: number }
export function readHit(r: FieldReader, out: HitMsg): boolean {
  if (!r.has(6)) return false;
  out.bossId = r.u16(); out.dmg = r.u32();
  return true;
}

// ── 클라 쪽 다른 플레이어 표(고정 크기, 보간용 이전/다음 위치) ──────────────

/** 다른 플레이어 한 칸. 렌더(peers.ts)는 active·visible인 칸만 그린다 */
export interface FieldPeer {
  /** 명단에 있는가(같은 채널) */
  active: boolean;
  /** 관심 영역 안인가(마지막 스냅에 있었는가) */
  visible: boolean;
  id: number;
  name: string;
  /** 0 카엘, 1 라이라 */
  hero: number;
  /** 환생 회차(이름표 별 개수) */
  cycle: number;
  /** 외형 묶음: 하위 4비트 무기 단계, 상위 4비트 갑옷 단계 */
  look: number;
  /** 보간 구간: (t0, x0, z0, f0) → (t1, x1, z1, f1). 시각은 클라 performance.now(ms) */
  t0: number; x0: number; z0: number; f0: number;
  t1: number; x1: number; z1: number; f1: number;
  /** 마지막 HeroAct */
  act: number;
  /** 명단에 들어온 순번(이름표 텍스처 갱신 판단 — 칸이 재사용되면 바뀐다) */
  serial: number;
}

export function makePeers(n = FIELD_CHANNEL_CAPACITY): FieldPeer[] {
  return Array.from({ length: n }, () => ({
    active: false, visible: false, id: 0, name: '', hero: 0, cycle: 0, look: 0,
    t0: 0, x0: 0, z0: 0, f0: 0, t1: 0, x1: 0, z1: 0, f1: 0, act: 0, serial: 0,
  }));
}

/** 보간 지연(ms): 스냅 한 주기 + 지터 여유 */
export const FIELD_INTERP_DELAY_MS = 120;

/** 보간 결과(호출한 쪽이 한 번 만든 것을 넘긴다) */
export interface PeerPose { x: number; z: number; facing: number; speed: number }
/**
 * now(ms)에서 보간 지연을 뺀 시각의 자세. 구간 끝을 넘으면 최대 0.2구간까지만 연장한다(멈춘 사람이 미끄러지지 않게).
 * speed는 구간 평균 속도(m/s) — 달리기/서기 고르기용.
 */
export function samplePeer(p: FieldPeer, now: number, out: PeerPose, delay = FIELD_INTERP_DELAY_MS): PeerPose {
  const span = p.t1 - p.t0;
  const dx = p.x1 - p.x0, dz = p.z1 - p.z0;
  if (span <= 0) { out.x = p.x1; out.z = p.z1; out.facing = p.f1; out.speed = 0; return out; }
  let a = (now - delay - p.t0) / span;
  a = a < 0 ? 0 : a > 1.2 ? 1.2 : a;
  out.x = p.x0 + dx * a;
  out.z = p.z0 + dz * a;
  let df = p.f1 - p.f0;
  if (df > Math.PI) df -= TAU; else if (df < -Math.PI) df += TAU;
  out.facing = p.f0 + df * Math.min(1, a);
  // 마지막 스냅이 오래됐으면(멈춤) 속도 0
  out.speed = now - delay - p.t1 > span * 1.5 ? 0 : Math.hypot(dx, dz) / (span / 1000);
  return out;
}
